import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchProjectById, fetchProjectMembers, fetchProjectStats,
  updateProject, addProjectMember, removeProjectMember, updateProjectMemberRole
} from '../../store/slices/projectsSlice';
import { fetchSprints } from '../../store/slices/sprintsSlice';
import { fetchUsers } from '../../store/slices/usersSlice';
import Modal from '../../components/common/Modal';
import { validationMessages } from '../../utils/validationMessages';
import ConfirmModal from '../../components/common/ConfirmModal';
import SearchableSelect from '../../components/common/SearchableSelect';
import ProjectTaskTypeManagement from '../../components/admin/ProjectTaskTypeManagement';
import api from '../../services/api';
import { generateAndDownloadReport } from '../../services/reporting';
import './ProjectSettings.css';

const ProjectSettings = ({ projectId, members, canManage }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentProject, stats } = useSelector((state) => state.projects);
  const { users } = useSelector((state) => state.users);
  const { sprints } = useSelector((state) => state.sprints);

  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  const [adding, setAdding] = useState(false);
  const [updatingRoleFor, setUpdatingRoleFor] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Workflow change state
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [workflowPreview, setWorkflowPreview] = useState(null);
  const [changingWorkflow, setChangingWorkflow] = useState(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [wfStep, setWfStep] = useState(1);
  const [statusCounts, setStatusCounts] = useState([]);
  const [newWorkflowStatuses, setNewWorkflowStatuses] = useState([]);
  const [statusMappings, setStatusMappings] = useState({});
  const [loadingMapping, setLoadingMapping] = useState(false);

  // Description editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);

  // Task Fields state
  const [taskFieldsMethod, setTaskFieldsMethod] = useState('time_tracking');
  const [taskFieldsMandatory, setTaskFieldsMandatory] = useState([]);
  const [savingTaskFields, setSavingTaskFields] = useState(false);

  // Project status state
  const [changingStatus, setChangingStatus] = useState(false);
  const [statusWarnings, setStatusWarnings] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [reportGenerating, setReportGenerating] = useState(false);

  // Report generation state
  const [reportScope, setReportScope] = useState('project');
  const [selectedSprintId, setSelectedSprintId] = useState('');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');

  const canChangeWorkflow = ['super_admin', 'admin'].includes(user?.role);
  const canChangeStatus = ['super_admin', 'admin'].includes(user?.role) ||
    members?.some(m => m.user?.id === user?.id && ['admin', 'manager', 'member'].includes(m.role));
  const canEditDescription = ['super_admin', 'admin'].includes(user?.role);
  const canGenerateFullReport = canChangeStatus;
  const canGenerateSprintReport = members?.some(m => m.user?.id === user?.id) || canGenerateFullReport;

  // Sync Task Fields state from currentProject when it loads or changes
  useEffect(() => {
    if (currentProject) {
      setTaskFieldsMethod(currentProject.time_tracking_method || 'time_tracking');
      setTaskFieldsMandatory(currentProject.mandatory_close_fields || []);
    }
  }, [currentProject?.id]); // eslint-disable-line

  useEffect(() => {
    dispatch(fetchUsers({ page: 1, pageSize: 100 }));
    dispatch(fetchSprints({ projectId }));
  }, [dispatch, projectId]);

  // Fetch available workflows
  const fetchWorkflows = useCallback(async () => {
    if (!canChangeWorkflow) return;
    setLoadingWorkflows(true);
    try {
      const response = await api.get('/workflows/');
      setWorkflows(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      setLoadingWorkflows(false);
    }
  }, [canChangeWorkflow]);

  const openWorkflowModal = () => {
    fetchWorkflows();
    setSelectedWorkflowId('');
    setWorkflowPreview(null);
    setWfStep(1);
    setStatusCounts([]);
    setNewWorkflowStatuses([]);
    setStatusMappings({});
    setShowWorkflowModal(true);
  };

  const handleWorkflowSelect = async (workflowId) => {
    setSelectedWorkflowId(workflowId);
    setWorkflowPreview(null);
    setStatusCounts([]);
    setNewWorkflowStatuses([]);
    setStatusMappings({});
    if (!workflowId) return;

    setLoadingMapping(true);
    try {
      const [previewRes, countsRes] = await Promise.all([
        api.get(`/workflows/projects/${projectId}/preview-change?workflow_id=${workflowId}`),
        api.get(`/workflows/projects/${projectId}/status-counts`)
      ]);
      const preview = previewRes.data;
      const counts = countsRes.data.statuses || [];
      const newStatuses = preview.new_statuses || [];

      setWorkflowPreview(preview);
      setStatusCounts(counts);
      setNewWorkflowStatuses(newStatuses);

      // Auto-map: exact key → same terminal/initial category → same category → initial fallback
      const initialKey = newStatuses.find(s => s.is_initial)?.key || newStatuses[0]?.key;
      const autoMappings = {};
      counts.forEach(old => {
        let match = newStatuses.find(ns => ns.key === old.key);
        if (!match && old.is_terminal) match = newStatuses.find(ns => ns.is_terminal);
        if (!match && old.is_initial) match = newStatuses.find(ns => ns.is_initial);
        if (!match) match = newStatuses.find(ns => ns.category === old.category);
        autoMappings[old.key] = match?.key || initialKey;
      });
      setStatusMappings(autoMappings);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load workflow details');
    } finally {
      setLoadingMapping(false);
    }
  };

  const handleChangeWorkflow = async () => {
    if (!selectedWorkflowId || !workflowPreview) return;

    setChangingWorkflow(true);
    try {
      const mappingsList = Object.entries(statusMappings).map(([old_status, new_status]) => ({
        old_status, new_status
      }));
      const response = await api.post(`/workflows/projects/${projectId}/change-workflow`, {
        workflow_id: parseInt(selectedWorkflowId),
        confirm_reset: true,
        status_mappings: mappingsList
      });
      toast.success(response.data.message || 'Workflow changed successfully');
      setShowWorkflowModal(false);
      dispatch(fetchProjectById(projectId));
      dispatch(fetchProjectStats(projectId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change workflow');
    } finally {
      setChangingWorkflow(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) {
      toast.error(validationMessages.general.userSelection);
      return;
    }

    setAdding(true);
    try {
      await dispatch(addProjectMember({
        projectId: parseInt(projectId),
        memberData: {
          user_id: parseInt(selectedUserId),
          role: selectedRole,
        },
      })).unwrap();
      toast.success('Member added successfully');
      setShowAddMemberModal(false);
      setSelectedUserId('');
      setSelectedRole('member');
    } catch (error) {
      toast.error(error || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = (userId, userName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Member',
      message: `Are you sure you want to remove "${userName}" from this project?`,
      confirmText: 'Remove',
      variant: 'warning',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(removeProjectMember({
            projectId: parseInt(projectId),
            userId,
          })).unwrap();
          toast.success('Member removed');
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error || 'Failed to remove member');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleRoleChange = async (userId, newRole, currentRole) => {
    if (newRole === currentRole) return;

    setUpdatingRoleFor(userId);
    try {
      await dispatch(updateProjectMemberRole({
        projectId: parseInt(projectId),
        userId,
        role: newRole,
      })).unwrap();
      toast.success('Role updated');
      dispatch(fetchProjectMembers(projectId));
    } catch (error) {
      toast.error(error || 'Failed to update role');
      dispatch(fetchProjectMembers(projectId));
    } finally {
      setUpdatingRoleFor(null);
    }
  };

  const handleEditDescription = () => {
    setEditedDescription(currentProject?.description || '');
    setIsEditingDescription(true);
  };

  const handleCancelDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  const handleSaveDescription = async () => {
    if (currentProject?.description && !editedDescription.trim()) {
      toast.error(validationMessages.project.description.empty);
      return;
    }

    if (editedDescription.length > 2000) {
      toast.error(validationMessages.project.description.maxLength);
      return;
    }

    setSavingDescription(true);
    try {
      await dispatch(updateProject({
        projectId: parseInt(projectId),
        projectData: { description: editedDescription.trim() || null }
      })).unwrap();
      toast.success('Description updated');
      setIsEditingDescription(false);
    } catch (error) {
      toast.error(error || 'Failed to update description');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleTaskFieldsMethodChange = (newMethod) => {
    setTaskFieldsMethod(newMethod);
    // Don't strip fields here — checkboxes for the other method are hidden,
    // so their state is preserved and restored when switching back.
    // Irrelevant fields are filtered out at save time.
  };

  const handleTaskFieldsMandatoryToggle = (field) => {
    setTaskFieldsMandatory(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const handleSaveTaskFields = async () => {
    setSavingTaskFields(true);
    try {
      // Only persist mandatory fields relevant to the active method
      const relevantMandatory = taskFieldsMandatory.filter(f => {
        if (f === 'story_points') return taskFieldsMethod === 'story_points';
        if (f === 'estimated_hours' || f === 'actual_hours') return taskFieldsMethod === 'time_tracking';
        return true;
      });
      await dispatch(updateProject({
        projectId: parseInt(projectId),
        projectData: {
          time_tracking_method: taskFieldsMethod,
          mandatory_close_fields: relevantMandatory,
        }
      })).unwrap();
      toast.success('Task field settings saved');
    } catch (error) {
      toast.error(error || 'Failed to save task field settings');
    } finally {
      setSavingTaskFields(false);
    }
  };

  const nonMembers = users.filter(u => !members.some(m => m.user.id === u.id));

  // Status configuration
  const STATUS_CONFIG = {
    active: { label: 'Active', class: 'badge-success', description: 'Full functionality enabled' },
    on_hold: { label: 'On Hold', class: 'badge-warning', description: 'Project temporarily paused' },
    completed: { label: 'Completed', class: 'badge-info', description: 'Project finished, limited editing' },
    archived: { label: 'Archived', class: 'badge-secondary', description: 'Read-only, hidden from default view' }
  };

  const handleStatusChangeRequest = async (newStatus) => {
    if (newStatus === currentProject?.status) return;
    
    // For completing/archiving, show confirmation with warnings
    if (['completed', 'archived'].includes(newStatus)) {
      try {
        const response = await api.get(`/projects/${projectId}/status-info`);
        setStatusWarnings(response.data.warnings);
        setPendingStatus(newStatus);
        setConfirmModal({
          isOpen: true,
          title: `${newStatus === 'completed' ? 'Complete' : 'Archive'} Project`,
          message: `Are you sure you want to ${newStatus === 'completed' ? 'complete' : 'archive'} this project?${
            response.data.warnings?.has_active_sprint 
              ? `\n\nWarning: This project has an active sprint "${response.data.warnings.active_sprint_name}".` 
              : ''
          }${
            response.data.warnings?.open_task_count > 0 
              ? `\n\nThere are ${response.data.warnings.open_task_count} open tasks.` 
              : ''
          }`,
          confirmText: newStatus === 'completed' ? 'Complete Project' : 'Archive Project',
          variant: 'warning',
          isLoading: false,
          onConfirm: () => executeStatusChange(newStatus)
        });
      } catch (error) {
        toast.error('Failed to check project status');
      }
    } else {
      executeStatusChange(newStatus);
    }
  };

  const executeStatusChange = async (newStatus) => {
    setChangingStatus(true);
    setConfirmModal(prev => ({ ...prev, isLoading: true }));
    try {
      await dispatch(updateProject({
        projectId: parseInt(projectId),
        projectData: { status: newStatus }
      })).unwrap();
      toast.success(`Project status changed to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      setPendingStatus(null);
      setStatusWarnings(null);
    } catch (error) {
      toast.error(error || 'Failed to change project status');
      setConfirmModal(prev => ({ ...prev, isLoading: false }));
    } finally {
      setChangingStatus(false);
    }
  };

  return (
    <div className="project-settings">
      <div className="settings-grid">
        {/* Stats */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h3>Statistics</h3>
          </div>
          <div className="settings-card-body">
            <div className="stat-items">
              <div className="stat-item">
                <span className="stat-label">Total Tasks</span>
                <span className="stat-value">{stats?.total_tasks || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Members</span>
                <span className="stat-value">{stats?.member_count || 0}</span>
              </div>
            </div>
            {stats?.tasks_by_status && (
              <div className="status-breakdown">
                <h4>By Status</h4>
                {Object.entries(stats.tasks_by_status).map(([status, count]) => (
                  <div key={status} className="breakdown-item">
                    <span>{status.replace('_', ' ')}</span>
                    <span>{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reporting */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h3>Reporting</h3>
          </div>
          <div className="settings-card-body">
            <p className="settings-help-text">
              Generate an executive-ready PDF report with ticket breakdowns, charts, and risk indicators.
            </p>

            {/* Report Scope Selection */}
            <div className="report-options">
              <label className="form-label">Report Type</label>
              <div className="report-scope-selector">
                <label className={`scope-option ${reportScope === 'project' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="reportScope"
                    value="project"
                    checked={reportScope === 'project'}
                    onChange={(e) => setReportScope(e.target.value)}
                    disabled={!canGenerateFullReport}
                  />
                  <span className="scope-label">Full Project</span>
                  <span className="scope-desc">All sprints and tickets</span>
                </label>
                <label className={`scope-option ${reportScope === 'sprint' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="reportScope"
                    value="sprint"
                    checked={reportScope === 'sprint'}
                    onChange={(e) => setReportScope(e.target.value)}
                    disabled={!canGenerateSprintReport || !sprints?.length}
                  />
                  <span className="scope-label">Sprint Report</span>
                  <span className="scope-desc">Single sprint summary</span>
                </label>
                <label className={`scope-option ${reportScope === 'date_range' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="reportScope"
                    value="date_range"
                    checked={reportScope === 'date_range'}
                    onChange={(e) => setReportScope(e.target.value)}
                    disabled={!canGenerateFullReport}
                  />
                  <span className="scope-label">Period</span>
                  <span className="scope-desc">Date-bounded report</span>
                </label>
              </div>

              {/* Sprint Selection */}
              {reportScope === 'sprint' && (
                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label className="form-label">Select Sprint</label>
                  <select
                    className="form-select"
                    style= {{ paddingBottom: '0px', paddingTop: '0px' }}
                    value={selectedSprintId}
                    onChange={(e) => setSelectedSprintId(e.target.value)}
                  >
                    <option value="">Choose a sprint...</option>
                    {sprints?.map((sprint) => (
                      <option key={sprint.id} value={sprint.id}>
                        {sprint.name} ({sprint.status})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Period Selection */}
              {reportScope === 'date_range' && (
                <div className="form-row" style={{ marginTop: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={reportStartDate}
                      onChange={(e) => setReportStartDate(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={reportEndDate}
                      onChange={(e) => setReportEndDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <button
              className="btn btn-primary"
              style={{ marginTop: '16px' }}
              disabled={
                reportGenerating ||
                (reportScope === 'project' && !canGenerateFullReport) ||
                (reportScope === 'sprint' && (!canGenerateSprintReport || !selectedSprintId)) ||
                (reportScope === 'date_range' && (!canGenerateFullReport || !reportStartDate || !reportEndDate))
              }
              onClick={async () => {
                try {
                  setReportGenerating(true);
                  await generateAndDownloadReport({
                    projectId,
                    scope: reportScope,
                    sprintId: reportScope === 'sprint' ? parseInt(selectedSprintId) : null,
                    startDateIso: reportScope === 'date_range' && reportStartDate ? new Date(reportStartDate).toISOString() : null,
                    endDateIso: reportScope === 'date_range' && reportEndDate ? new Date(reportEndDate).toISOString() : null,
                    setLoading: () => {},
                  });
                  toast.success('Report generated successfully.');
                } catch (error) {
                  toast.error(
                    error?.response?.data?.detail ||
                      'Failed to generate report.'
                  );
                } finally {
                  setReportGenerating(false);
                }
              }}
            >
              {reportGenerating ? 'Generating Report…' : 'Generate PDF Report'}
            </button>
            
            {!canGenerateFullReport && !canGenerateSprintReport && (
              <p className="settings-help-text muted" style={{ marginTop: '8px' }}>
                Only project members can generate reports.
              </p>
            )}
          </div>
        </div>

        {/* Workflow Settings */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h3>Workflow</h3>
            {canChangeWorkflow && (
              <button className="btn btn-sm btn-primary" onClick={openWorkflowModal}>
                Change Workflow
              </button>
            )}
          </div>
          <div className="settings-card-body">
            <div className="workflow-info-section">
              <div className="workflow-current">
                <span className="workflow-label">Current Workflow:</span>
                <span className="workflow-name">
                  {currentProject?.workflow?.name || 'No workflow assigned'}
                </span>
              </div>
              {currentProject?.workflow && (
                <div className="workflow-statuses-preview">
                  <span className="statuses-label">Statuses:</span>
                  <span className="statuses-count">
                    {currentProject.workflow.status_count} statuses defined
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Project Status */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h3>Project Status</h3>
          </div>
          <div className="settings-card-body">
            <div className="status-section">
              <div className="current-status">
                <span className="status-label">Current Status:</span>
                <span className={`badge ${STATUS_CONFIG[currentProject?.status]?.class || 'badge-primary'}`}>
                  {STATUS_CONFIG[currentProject?.status]?.label || currentProject?.status}
                </span>
              </div>
              <p className="status-description">
                {STATUS_CONFIG[currentProject?.status]?.description}
              </p>
              {canChangeStatus && (
                <div className="status-change-section">
                  <label className="form-label">Change Status</label>
                  <select
                    className="form-select"
                    value={currentProject?.status || 'active'}
                    onChange={(e) => handleStatusChangeRequest(e.target.value)}
                    disabled={changingStatus}
                  >
                    <option value="active">Active - Full functionality</option>
                    <option value="on_hold">On Hold - Temporarily paused</option>
                    <option value="completed">Completed - Project finished</option>
                    <option value="archived">Archived - Read-only</option>
                  </select>
                  <p className="status-help-text">
                    Changing status affects what actions can be performed on this project.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="settings-card">
          <div className="settings-card-header">
            <h3>Description</h3>
            {canEditDescription && !isEditingDescription && (
              <button className="btn btn-sm btn-ghost" onClick={handleEditDescription}>
                Edit
              </button>
            )}
          </div>
          <div className="settings-card-body">
            {isEditingDescription ? (
              <div className="description-edit-form">
                <textarea
                  className="form-input description-textarea"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  placeholder="Enter project description..."
                  rows={4}
                  maxLength={2000}
                  autoFocus
                />
                <div className="description-edit-footer">
                  <span className="char-count">{editedDescription.length}/2000</span>
                  <div className="description-edit-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleCancelDescription}
                      disabled={savingDescription}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleSaveDescription}
                      disabled={savingDescription}
                    >
                      {savingDescription ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="description-text">
                {currentProject?.description || 'No description provided.'}
              </p>
            )}
          </div>
        </div>

        {/* Task Fields */}
        {canManage && (
          <div className="settings-card settings-card-full">
            <div className="settings-card-header">
              <h3>Task Fields</h3>
            </div>
            <div className="settings-card-body">
              {/* Time Tracking Method */}
              <div className="task-fields-section">
                <label className="task-fields-section-label">Time Tracking Method</label>
                <p className="task-fields-section-desc">
                  Choose how effort is estimated across tasks in this project.
                </p>
                <div className="task-fields-method-options">
                  <label className={`task-fields-method-option${taskFieldsMethod === 'time_tracking' ? ' active' : ''}`}>
                    <input
                      type="radio"
                      name="timeTrackingMethod"
                      value="time_tracking"
                      checked={taskFieldsMethod === 'time_tracking'}
                      onChange={() => handleTaskFieldsMethodChange('time_tracking')}
                    />
                    <div className="task-fields-method-content">
                      <span className="task-fields-method-title">Time Tracking</span>
                      <span className="task-fields-method-desc">Use estimated and actual hours</span>
                    </div>
                  </label>
                  <label className={`task-fields-method-option${taskFieldsMethod === 'story_points' ? ' active' : ''}`}>
                    <input
                      type="radio"
                      name="timeTrackingMethod"
                      value="story_points"
                      checked={taskFieldsMethod === 'story_points'}
                      onChange={() => handleTaskFieldsMethodChange('story_points')}
                    />
                    <div className="task-fields-method-content">
                      <span className="task-fields-method-title">Story Points</span>
                      <span className="task-fields-method-desc">Use points for estimation</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Mandatory Fields on Close */}
              <div className="task-fields-section task-fields-section-divider">
                <label className="task-fields-section-label">Mandatory Fields on Close</label>
                <p className="task-fields-section-desc">
                  Fields that must be filled before a task can be moved to a done status.
                </p>
                <div className="task-fields-checkboxes">
                  <label className="task-fields-checkbox-option">
                    <input
                      type="checkbox"
                      checked={taskFieldsMandatory.includes('assignee')}
                      onChange={() => handleTaskFieldsMandatoryToggle('assignee')}
                    />
                    <span className="task-fields-checkbox-label">Assignee</span>
                  </label>

                  {taskFieldsMethod === 'story_points' && (
                    <label className="task-fields-checkbox-option">
                      <input
                        type="checkbox"
                        checked={taskFieldsMandatory.includes('story_points')}
                        onChange={() => handleTaskFieldsMandatoryToggle('story_points')}
                      />
                      <span className="task-fields-checkbox-label">Story Points</span>
                    </label>
                  )}

                  {taskFieldsMethod === 'time_tracking' && (
                    <>
                      <label className="task-fields-checkbox-option">
                        <input
                          type="checkbox"
                          checked={taskFieldsMandatory.includes('estimated_hours')}
                          onChange={() => handleTaskFieldsMandatoryToggle('estimated_hours')}
                        />
                        <span className="task-fields-checkbox-label">Estimated Hours</span>
                      </label>
                      <label className="task-fields-checkbox-option">
                        <input
                          type="checkbox"
                          checked={taskFieldsMandatory.includes('actual_hours')}
                          onChange={() => handleTaskFieldsMandatoryToggle('actual_hours')}
                        />
                        <span className="task-fields-checkbox-label">Actual Hours</span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              <div className="task-fields-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveTaskFields}
                  disabled={savingTaskFields}
                >
                  {savingTaskFields ? 'Saving...' : 'Save Task Fields'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Members */}
        <div className="settings-card settings-card-full">
          <div className="settings-card-header">
            <div className="members-header-left">
              <h3>Team Members</h3>
              <span className="members-count-badge">{members.length}</span>
            </div>
            <div className="members-header-right">
              <div className="members-search-box">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" className="members-search-icon">
                  <path fillRule="evenodd" d="M9.965 11.026a5 5 0 111.06-1.06l2.755 2.754a.75.75 0 11-1.06 1.06l-2.755-2.754zM10.5 7a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" clipRule="evenodd"/>
                </svg>
                <input
                  type="text"
                  className="members-search-input"
                  placeholder="Search members..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
                {memberSearch && (
                  <button className="members-search-clear" onClick={() => setMemberSearch('')} title="Clear">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
                    </svg>
                  </button>
                )}
              </div>
              {canManage && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowAddMemberModal(true)}
                >
                  Add Member
                </button>
              )}
            </div>
          </div>
          <div className="settings-card-body" style={{ padding: 0 }}>
            <div className="members-list members-list-scrollable">
              {members
                .filter((member) => {
                  if (!memberSearch.trim()) return true;
                  const q = memberSearch.toLowerCase();
                  const fullName = `${member.user.first_name} ${member.user.last_name}`.toLowerCase();
                  return fullName.includes(q) || member.user.email.toLowerCase().includes(q);
                })
                .map((member) => (
                  <div key={member.id} className="member-item">
                    <div className="member-info">
                      <div className="avatar">
                        {member.user.first_name.charAt(0)}{member.user.last_name.charAt(0)}
                      </div>
                      <div className="member-details">
                        <span className="member-name">
                          {member.user.first_name} {member.user.last_name}
                        </span>
                        <span className="member-email">{member.user.email}</span>
                      </div>
                    </div>
                    <div className="member-actions">
                      <span className="badge badge-primary">{member.role}</span>
                      {canManage && (
                        <button
                          className="btn btn-sm btn-ghost btn-danger-hover"
                          style={{ background: '#fb0000', color: 'white' }}
                          onClick={() => handleRemoveMember(
                            member.user.id,
                            `${member.user.first_name} ${member.user.last_name}`
                          )}
                          disabled={['super_admin', 'admin'].includes(member.user.role)}
                          title={['super_admin', 'admin'].includes(member.user.role) ? 'Admin users cannot be removed from projects' : ''}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              {members.filter((member) => {
                if (!memberSearch.trim()) return true;
                const q = memberSearch.toLowerCase();
                const fullName = `${member.user.first_name} ${member.user.last_name}`.toLowerCase();
                return fullName.includes(q) || member.user.email.toLowerCase().includes(q);
              }).length === 0 && (
                <div className="members-empty-search">
                  No members match "<strong>{memberSearch}</strong>"
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Task Type Management */}
        {['super_admin', 'admin'].includes(user?.role) && (
          <div className="settings-card settings-card-full">
            <div className="settings-card-header">
              <h3>Task Types</h3>
            </div>
            <div className="settings-card-body">
              <ProjectTaskTypeManagement projectId={projectId} />
            </div>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      <Modal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        title="Add Team Member"
        size="small"
      >
        <div className="form-group">
          <label className="form-label">Select User</label>
          <SearchableSelect
            options={nonMembers.map(u => ({
              value: u.id,
              label: `${u.first_name} ${u.last_name}`,
              email: u.email
            }))}
            value={selectedUserId}
            onChange={(val) => {
              setSelectedUserId(val);
              // Reset role to 'member' when user changes to avoid invalid role selection
              setSelectedRole('member');
            }}
            placeholder="Choose a user..."
            searchPlaceholder="Search users..."
            getOptionDescription={(opt) => opt.email}
            clearable={true}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Role</label>
          <select
            className="form-select"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            {(() => {
              const selectedUser = nonMembers.find(u => u.id === Number(selectedUserId));
              const sysRole = selectedUser?.role;
              const canBeAdmin = ['super_admin', 'admin'].includes(sysRole);
              const canBeManager = ['super_admin', 'admin', 'manager'].includes(sysRole);
              return (
                <>
                  {canBeAdmin && <option value="admin">Admin</option>}
                  {canBeManager && <option value="manager">Manager</option>}
                  <option value="member">Member</option>
                </>
              );
            })()}
          </select>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowAddMemberModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleAddMember} disabled={adding}>
            {adding ? 'Adding...' : 'Add Member'}
          </button>
        </div>
      </Modal>

      {/* Change Workflow Modal — 2-step wizard */}
      <Modal
        isOpen={showWorkflowModal}
        onClose={() => { setShowWorkflowModal(false); setWfStep(1); }}
        title="Change Project Workflow"
        size="large"
      >
        {/* Step indicator */}
        <div className="wf-wizard-steps">
          <div className={`wf-wizard-step${wfStep >= 1 ? ' wf-step-active' : ''}`}>
            <span className="wf-step-num">1</span>
            <span className="wf-step-label">Select Workflow</span>
          </div>
          <div className="wf-step-connector" />
          <div className={`wf-wizard-step${wfStep >= 2 ? ' wf-step-active' : ''}`}>
            <span className="wf-step-num">2</span>
            <span className="wf-step-label">Map Statuses</span>
          </div>
        </div>

        {/* ── Step 1: Select workflow ── */}
        {wfStep === 1 && (
          <div className="wf-step-body">
            <div className="wf-from-to-row">
              <div className="wf-from-box">
                <div className="wf-box-label">Current workflow</div>
                <div className="wf-box-name">{currentProject?.workflow?.name || 'None'}</div>
                <div className="wf-box-statuses">
                  {(currentProject?.workflow?.statuses || [])
                    .slice().sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map(s => (
                      <span key={s.key} className="wf-status-pill" style={{ background: (s.color || '#9ca3af') + '22', color: s.color || '#9ca3af', borderColor: (s.color || '#9ca3af') + '55' }}>
                        {s.name}
                      </span>
                    ))}
                </div>
              </div>
              <div className="wf-arrow-divider">→</div>
              <div className="wf-to-box">
                <div className="wf-box-label">New workflow</div>
                {selectedWorkflowId && workflowPreview ? (
                  <>
                    <div className="wf-box-name">{workflowPreview.new_workflow_name}</div>
                    <div className="wf-box-statuses">
                      {newWorkflowStatuses.map(s => (
                        <span key={s.key} className="wf-status-pill" style={{ background: (s.color || '#9ca3af') + '22', color: s.color || '#9ca3af', borderColor: (s.color || '#9ca3af') + '55' }}>
                          {s.name}
                          {s.is_initial && <span className="wf-pill-tag">Initial</span>}
                          {s.is_terminal && <span className="wf-pill-tag">Done</span>}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="wf-box-placeholder">{loadingMapping ? 'Loading…' : 'Select a workflow below'}</div>
                )}
              </div>
            </div>

            <div className="workflow-section" style={{ marginTop: '16px' }}>
              <label className="workflow-field-label">Select new workflow *</label>
              <select
                className="form-select"
                value={selectedWorkflowId || ''}
                onChange={(e) => handleWorkflowSelect(e.target.value)}
                disabled={loadingWorkflows}
              >
                <option value="">Choose a workflow…</option>
                {workflows
                  .filter(w => w.id !== currentProject?.workflow?.id)
                  .map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.status_count} statuses{w.is_default ? ' · Default' : ''})
                    </option>
                  ))}
              </select>
            </div>
          </div>
        )}

        {/* ── Step 2: Map statuses ── */}
        {wfStep === 2 && (
          <div className="wf-step-body">
            <p className="wf-step-desc">
              Choose where tasks in each current status should land in <strong>{workflowPreview?.new_workflow_name}</strong>.
              We've auto-suggested the closest match — adjust any that need it.
            </p>

            <div className="wf-mapping-table">
              <div className="wf-mapping-header">
                <span>Current status</span>
                <span />
                <span>Move tasks to</span>
              </div>
              {statusCounts.map(old => (
                <div className="wf-mapping-row" key={old.key}>
                  <div className="wf-status-chip" style={{ borderColor: (old.color || '#9ca3af') + '88' }}>
                    <span className="wf-status-dot" style={{ background: old.color || '#9ca3af' }} />
                    <span className="wf-chip-name">{old.name}</span>
                    <span className="wf-chip-count">{old.task_count}</span>
                  </div>
                  <span className="wf-row-arrow">→</span>
                  <select
                    className="form-select wf-mapping-select"
                    value={statusMappings[old.key] || ''}
                    onChange={(e) => setStatusMappings(prev => ({ ...prev, [old.key]: e.target.value }))}
                  >
                    {newWorkflowStatuses.map(ns => (
                      <option key={ns.key} value={ns.key}>
                        {ns.name}{ns.is_initial ? ' (Initial)' : ''}{ns.is_terminal ? ' (Done)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="wf-summary-bar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{workflowPreview?.affected_tasks ?? 0} tasks will be migrated. This cannot be undone.</span>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: 0 }} onClick={() => { setShowWorkflowModal(false); setWfStep(1); }}>
            Cancel
          </button>
          {wfStep === 1 ? (
            <button
              className="btn btn-primary"
              disabled={!selectedWorkflowId || loadingMapping || !workflowPreview}
              onClick={() => setWfStep(2)}
            >
              {loadingMapping ? 'Loading…' : 'Next: Map Statuses →'}
            </button>
          ) : (
            <>
              <button className="btn btn-secondary" style={{ marginTop: 0 }} onClick={() => setWfStep(1)}>
                ← Back
              </button>
              <button
                className="btn btn-danger"
                onClick={handleChangeWorkflow}
                disabled={changingWorkflow || statusCounts.some(s => !statusMappings[s.key])}
              >
                {changingWorkflow ? 'Applying…' : 'Apply Changes'}
              </button>
            </>
          )}
        </div>
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default ProjectSettings;
